import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Typography,
  Stack,
  Button,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import trainings from "./data/trainings";

const BRAND = "#125570";

function TagChips({ tags, onClickTag }) {
  return (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
      {tags.map((t) => (
        <Chip
          key={t}
          label={t}
          size="small"
          onClick={onClickTag ? () => onClickTag(t) : undefined}
          sx={{
            fontWeight: 800,
            bgcolor: "rgba(18, 85, 112, 0.10)",
            color: "primary.main",
          }}
        />
      ))}
    </Stack>
  );
}

function TrainingCard({ training, onOpen }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 12px 32px rgba(2, 6, 23, 0.08)",
        transition: "transform .15s ease, box-shadow .15s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 18px 46px rgba(2, 6, 23, 0.12)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(18, 85, 112, 0.10)",
                  color: "primary.main",
                }}
              >
                <SchoolIcon />
              </Box>
              <Box>
                <Typography fontWeight={950} sx={{ lineHeight: 1.15 }}>
                  {training.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {training.subtitle}
                </Typography>
              </Box>
            </Stack>

            {training.featured ? (
              <Chip
                icon={<TrendingUpIcon />}
                label="Destaque"
                size="small"
                sx={{ fontWeight: 900 }}
              />
            ) : null}
          </Stack>

          <Divider />

          <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {training.description}
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<AccessTimeIcon />}
              label={training.duration}
              size="small"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              icon={<VerifiedIcon />}
              label={training.level}
              size="small"
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          <TagChips tags={training.tags} />

          <Box sx={{ pt: 0.5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onOpen}
              endIcon={<ArrowForwardIcon />}
              sx={{ fontWeight: 950 }}
            >
              Abrir treinamento
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function TrainingCatalog() {
  const navigate = useNavigate();

  const allTags = React.useMemo(() => {
    const set = new Set();
    trainings.forEach((t) => t.tags.forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, []);

  const [q, setQ] = React.useState("");
  const [activeTag, setActiveTag] = React.useState("");

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    return trainings.filter((t) => {
      const matchesText =
        !query ||
        t.title.toLowerCase().includes(query) ||
        t.subtitle.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.tags.some((tag) => tag.toLowerCase().includes(query));

      const matchesTag = !activeTag || t.tags.includes(activeTag);
      return matchesText && matchesTag;
    });
  }, [q, activeTag]);

  const featured = trainings.find((t) => t.featured);

  return (
    <Box sx={{ bgcolor: "#F6F8FB", minHeight: "100vh" }}>
      {/* <AppBar position="sticky" color="default" sx={{ boxShadow: "none" }}>
        <Toolbar sx={{ borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: BRAND,
              color: "white",
              display: "grid",
              placeItems: "center",
              fontWeight: 950,
              mr: 1.5,
            }}
          >
            E
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={950} lineHeight={1.1}>
              Treinamentos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecione um treinamento para iniciar
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={() => window.open("https://wa.me/5511971301709", "_blank")}
          >
            Contato
          </Button>
        </Toolbar>
      </AppBar> */}

      <Container sx={{ py: { xs: 3, md: 5 } }}>
        {/* HERO */}
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: "1px solid rgba(15, 23, 42, 0.10)",
            background:
              "linear-gradient(180deg, rgba(18, 85, 112, 0.12), rgba(255,255,255, 1))",
            boxShadow: "0 18px 46px rgba(2, 6, 23, 0.10)",
            mb: 3,
          }}
        >
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={1.4}>
                <Chip
                  label="Catálogo de Treinamentos"
                  sx={{
                    width: "fit-content",
                    fontWeight: 900,
                    bgcolor: "rgba(18, 85, 112, 0.12)",
                    color: "primary.main",
                  }}
                />
                <Typography variant="h3" fontWeight={950} sx={{ letterSpacing: -0.8 }}>
                  Aprenda com foco em resultado e acreditação
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.9, maxWidth: 760 }}>
                  Conteúdos organizados em formato de apresentação (modo slides), com navegação,
                  fullscreen e estrutura executiva. Escolha um treinamento abaixo para começar.
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={1.2}>
                <TextField
                  label="Buscar treinamento"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  fullWidth
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={activeTag ? `Filtro: ${activeTag}` : "Sem filtro"}
                    onDelete={activeTag ? () => setActiveTag("") : undefined}
                    sx={{ fontWeight: 900 }}
                  />
                  <Chip
                    label="Limpar busca"
                    onClick={() => setQ("")}
                    sx={{ fontWeight: 900 }}
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* TAG FILTERS */}
        <Paper sx={{ p: 2, borderRadius: 4, mb: 3 }}>
          <Typography fontWeight={950} sx={{ mb: 1 }}>
            Filtrar por tema
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {allTags.map((t) => (
              <Chip
                key={t}
                label={t}
                onClick={() => setActiveTag((cur) => (cur === t ? "" : t))}
                variant={activeTag === t ? "filled" : "outlined"}
                sx={{
                  fontWeight: 900,
                  ...(activeTag === t
                    ? { bgcolor: "rgba(18, 85, 112, 0.12)", color: "primary.main" }
                    : {}),
                }}
              />
            ))}
          </Stack>
        </Paper>

        {/* FEATURED */}
        {featured ? (
          <Paper
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 4,
              mb: 3,
              border: `1px solid rgba(18, 85, 112, 0.20)`,
              bgcolor: "rgba(18, 85, 112, 0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography fontWeight={950} sx={{ mb: 0.3 }}>
                  Destaque
                </Typography>
                <Typography variant="h5" fontWeight={950}>
                  {featured.title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.85 }}>
                  {featured.subtitle}
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(featured.route)}
                sx={{ fontWeight: 950 }}
              >
                Iniciar agora
              </Button>
            </Stack>
          </Paper>
        ) : null}

        {/* GRID */}
        <Grid container spacing={2.5}>
          {filtered.map((t) => (
            <Grid item xs={12} md={6} lg={4} key={t.id}>
              <TrainingCard training={t} onOpen={() => navigate(t.route)} />
            </Grid>
          ))}
        </Grid>

        {!filtered.length ? (
          <Paper sx={{ p: 3, borderRadius: 4, mt: 3 }}>
            <Typography fontWeight={950}>Nenhum treinamento encontrado</Typography>
            <Typography color="text.secondary">
              Tente alterar a busca ou remover filtros.
            </Typography>
          </Paper>
        ) : null}
      </Container>
    </Box>
  );
}
